import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/state/AppContext";
import { usePageTitle } from "@/lib/usePageTitle";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { StatusTimeline } from "@/components/StatusTimeline";
import { PaymentPanel } from "@/components/PaymentPanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { rupees, shortOrderRef } from "@/lib/format";
import { isUuid } from "@/lib/ids";

// The non-terminal order statuses (ORDER_TRANSITIONS in core/enums.py): keep polling
// until the order reaches completed / cancelled / refunded. Must stay in step with the
// backend vocabulary — `ready_for_pickup` and `delivered` still await a staff action.
const OPEN_STATUSES = [
  "placed",
  "confirmed",
  "preparing",
  "ready_for_pickup",
  "out_for_delivery",
  "delivered",
];
const CANCELLABLE = ["placed", "confirmed"];
const POLL_MS = 8000;

/** Citizen order tracking — `/order/:id` (the target an order notification link opens). */
export default function OrderDetail() {
  const { id } = useParams();
  const { t, lang } = useApp();
  usePageTitle(t("commerce.detailTitle")); // R4-7: per-page tab title
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | missing | error
  const [loadError, setLoadError] = useState("");
  const [payment, setPayment] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    // A short display ref ("A36118" — `shortOrderRef` is lossy) is not an id. Don't hand it to a
    // UUID endpoint (422 shown raw): go straight to the friendly "not found" state.
    if (!isUuid(id)) {
      setState("missing");
      return;
    }
    try {
      const [res, pay] = await Promise.all([
        api.get(`/orders/${id}`),
        api.get("/payments/mine").catch(() => null),
      ]);
      const found = res.data;
      setItem(found);
      setPayment(
        pay
          ? rows(pay.data).find(
              (p) => p.source_type === "order" && p.source_id === found.id,
            ) || null
          : null,
      );
      setState("ready");
    } catch (e) {
      if (e?.response?.status === 404 || e?.response?.status === 403) {
        setState("missing");
        return;
      }
      // A blip during background polling must not blank a page the citizen is already reading.
      setLoadError(apiErrorMessage(e, ""));
      setState((s) => (s === "ready" ? s : "error"));
    }
  }, [id]);

  useEffect(() => {
    setState("loading");
    load();
  }, [load]);

  // A UPI payment has no gateway callback; keep asking while it is open.
  useEffect(() => {
    if (
      !payment ||
      !["pending", "awaiting_confirmation"].includes(payment.status)
    )
      return;
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [payment?.status, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep asking while the thing is live: staff confirm on their side, the citizen sees it land here.
  useEffect(() => {
    if (!item || !OPEN_STATUSES.includes(item.status)) return;
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [item?.status, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const cancel = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/orders/${id}/transition`, { status: "cancelled" });
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  const claimPayment = async (utr) => {
    setPaymentBusy(true);
    setPaymentError("");
    try {
      await api.post(`/payments/${payment.id}/claim`, { utr });
      await load();
    } catch (e) {
      setPaymentError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setPaymentBusy(false);
    }
  };

  const cancelPayment = async () => {
    setPaymentBusy(true);
    setPaymentError("");
    try {
      await api.post(`/payments/${payment.id}/cancel`);
      await load();
    } catch (e) {
      setPaymentError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setPaymentBusy(false);
    }
  };

  if (state === "loading")
    return (
      <div
        className="mx-auto max-w-2xl space-y-4 px-5 py-12 sm:px-8"
        data-testid="order-skeleton"
        aria-busy="true"
      >
        <div className="h-28 animate-pulse rounded-2xl bg-muted" />
        <div className="h-40 animate-pulse rounded-2xl bg-muted" />
      </div>
    );
  if (state === "missing")
    return (
      <div
        className="mx-auto max-w-2xl px-5 py-24 text-center"
        data-testid="order-not-found"
      >
        <p className="text-lg">{t("commerce.notFound")}</p>
        <Button
          className="mt-6 rounded-full"
          onClick={() => navigate("/activity")}
        >
          {t("commerce.backToActivity")}
        </Button>
      </div>
    );
  if (state === "error")
    return (
      <div
        className="mx-auto max-w-2xl px-5 py-24 text-center"
        data-testid="order-error"
      >
        <p className="text-lg text-destructive">
          {loadError || t("commerce.loadFailed")}
        </p>
        <Button
          className="mt-6 rounded-full"
          onClick={() => {
            setState("loading");
            load();
          }}
          data-testid="order-retry-btn"
        >
          {t("common.retry")}
        </Button>
      </div>
    );

  const ps = item.pricing_snapshot || {};
  const subtotal = ps.subtotal ?? item.total_minor_units ?? 0;
  const offer = ps.offer_discount || 0;
  const points = ps.discount || 0;
  const total = item.total_minor_units ?? ps.total ?? subtotal;

  return (
    <div
      className="mx-auto max-w-2xl px-5 py-12 sm:px-8"
      data-testid="order-page"
    >
      <div className="rounded-2xl bg-[hsl(var(--primary))] p-6 text-primary-foreground sm:p-8">
        <p className="text-sm opacity-80">
          {t("commerce.detailTitle")}
          {/* R5-11: the same short ref the activity list and the owner's order show. */}
          <span className="ml-2 tabular-nums" data-testid="order-number">#{shortOrderRef(item.id)}</span>
        </p>
        <h1
          className="mt-1 font-heading text-3xl font-bold tracking-tight"
          data-testid="order-headline"
        >
          {t("commerce.statusLabels")[item.status] || item.status}
        </h1>
      </div>

      <div className="mt-6 space-y-6">
        <div className="rounded-2xl border border-border p-5">
          <p
            className="font-heading text-lg font-semibold"
            data-testid="order-title"
          >
            {item.business_name}
          </p>
          <StatusTimeline
            timeline={item.timeline}
            labelsKey="commerce.statusLabels"
            testId="order-timeline"
          />
        </div>

        <div className="rounded-2xl border border-border p-5 text-sm">
          <Row
            label={t("commerce.fulfilment")}
            value={t(`commerce.fulfilmentTypes.${item.fulfillment_type}`)}
          />
          {item.address_snapshot && (
            <Row
              label={t("commerce.address")}
              value={[item.address_snapshot, item.landmark_snapshot]
                .filter(Boolean)
                .join(" · ")}
            />
          )}
          {item.notes && <Row label={t("commerce.notes")} value={item.notes} />}
          {item.payment_mode && (
            <Row
              label={t("payment.modeLabel")}
              value={t(`payment.${item.payment_mode}Mode`)}
            />
          )}
          <Row
            label={t("commerce.orderStatus")}
            value={
              <Badge data-testid="order-status-badge">
                {t("commerce.statusLabels")[item.status] || item.status}
              </Badge>
            }
          />
        </div>

        <div
          className="rounded-2xl border border-border p-5"
          data-testid="order-items"
        >
          <p className="font-semibold">{t("commerce.itemsTitle")}</p>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            {(item.items_snapshot || []).map((i, idx) => (
              <li key={idx}>
                {i.quantity} × {i.name} —{" "}
                {rupees({
                  minor_units: (i.unit_price_minor_units ?? 0) * i.quantity,
                })}
              </li>
            ))}
          </ul>
          <div className="mt-3 space-y-0.5 text-sm tabular-nums">
            <p
              className="flex justify-between text-muted-foreground"
              data-testid="order-subtotal"
            >
              <span>{t("commerce.subtotal")}</span>
              <span>{rupees({ minor_units: subtotal })}</span>
            </p>
            {offer > 0 && (
              <p
                className="flex justify-between text-muted-foreground"
                data-testid="order-offer-discount"
              >
                <span>{t("commerce.offerDiscount")}</span>
                <span>− {rupees({ minor_units: offer })}</span>
              </p>
            )}
            {points > 0 && (
              <p
                className="flex justify-between text-muted-foreground"
                data-testid="order-points-discount"
              >
                <span>{t("commerce.pointsDiscount")}</span>
                <span>− {rupees({ minor_units: points })}</span>
              </p>
            )}
            <p
              className="flex justify-between font-semibold"
              data-testid="order-total"
            >
              <span>{t("commerce.total")}</span>
              <span>{rupees({ minor_units: total })}</span>
            </p>
          </div>
        </div>

        {/* RF-23: an unpaid UPI request on an order that has since been refunded/cancelled would
            show "I've paid" beside a Refunded badge until a reload - the order is the authority. */}
        {payment && !(payment.status === "pending" && ["cancelled", "refunded"].includes(item.status)) && (
          <PaymentPanel
            payment={payment}
            requireUtr={payment.kind === "checkout"}
            onClaim={claimPayment}
            onCancel={cancelPayment}
            busy={paymentBusy}
            error={paymentError}
            testId="order-payment"
          />
        )}

        {error && (
          <p className="text-sm text-destructive" data-testid="order-error-msg">
            {error}
          </p>
        )}

        {CANCELLABLE.includes(item.status) && (
          <Button
            variant="outline"
            className="rounded-full"
            disabled={busy}
            onClick={() => setConfirmingCancel(true)}
            data-testid="order-cancel-btn"
          >
            {t("commerce.cancelOrder")}
          </Button>
        )}
        <ConfirmDialog
          open={confirmingCancel}
          onOpenChange={setConfirmingCancel}
          title={t("commerce.cancelConfirmTitle")}
          body={t("commerce.cancelConfirmBody")}
          confirmLabel={t("commerce.cancelConfirmYes")}
          cancelLabel={t("commerce.cancelKeep")}
          destructive
          busy={busy}
          onConfirm={async () => {
            await cancel();
            setConfirmingCancel(false);
          }}
          testIdPrefix="cancel-confirm"
        />
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-right">{value}</span>
    </div>
  );
}
